import React, { useState, useEffect } from "react";
import { SwipeableDrawer, useMediaQuery, Box } from "@mui/material";
import styled from "styled-components";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import { useClientSidePanel } from "../../../../providers/ClientSidePanelProvider";
import NewPrescriptionWidget from "../prescriptionBuilder/NewPrescriptionWidget";

// Constants
const DRAG_THRESHOLD_PX = 50;
const MOBILE_BREAKPOINT = 600;
const DESKTOP_SIDEBAR_WIDTH = 600;
const BOTTOM_SHEET_HEIGHT = "70vh";

const DesktopPanelWrapper = styled.div<{ $isOpen: boolean; theme: Theme }>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: ${DESKTOP_SIDEBAR_WIDTH}px;
  background-color: ${({ theme }) => theme.colors_ds.NEUTRAL_BG_MAIN};
  transform: ${({ $isOpen }) =>
    $isOpen ? "translateX(0)" : "translateX(100%)"};
  transition: transform 0.3s ease-in-out;
  z-index: 1200;
  box-shadow: ${({ $isOpen }) =>
    $isOpen ? "-2px 0 8px rgba(0, 0, 0, 0.1)" : "none"};
  overflow-y: auto;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: none;
  }
`;

const MobileBottomSheetContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const DragHandleContainer = styled.div<{ $isDragging: boolean; theme: Theme }>`
  display: flex;
  justify-content: center;
  cursor: ${({ $isDragging }) => ($isDragging ? "grabbing" : "grab")};
  flex-shrink: 0;
  user-select: none;
  touch-action: none;
`;

const DragHandle = styled.div<{ theme: Theme }>`
  margin-top: 16px;
  width: 48px;
  height: 3px;
  border-radius: 20px;
  background-color: ${({ theme }) => theme.colors_ds.NEUTRAL_BORDER_DEFAULT};
`;

const MobileBottomSheetBody = styled.div`
  flex: 1;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 5px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #abaaaa;
  }

  scrollbar-color: #f1f1f1 transparent;
  scrollbar-width: thin;
`;

const PlaceholderContent = styled.div<{ theme: Theme }>`
  padding: 24px;
  color: ${({ theme }) => theme.colors_ds.NEUTRAL_TEXT_DEFAULT};
  font-family: var(--font-Medium);
`;

const Backdrop = styled.div<{ $isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1199;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  visibility: ${({ $isVisible }) => ($isVisible ? "visible" : "hidden")};
  transition: opacity 0.3s ease-in-out, visibility 0.3s ease-in-out;
  cursor: pointer;
`;

const ClientSidePanel: React.FC = () => {
  const theme: Theme = useTheme();
  const { isOpen, closePanel, editingPrescription } = useClientSidePanel();
  const isDesktop = useMediaQuery(`(min-width:${MOBILE_BREAKPOINT + 1}px)`);

  // Drag to close state for mobile
  const [dragStartY, setDragStartY] = useState<number | null>(null);
  const [dragCurrentY, setDragCurrentY] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Handle drag start
  const handleDragStart = (clientY: number) => {
    setDragStartY(clientY);
    setDragCurrentY(clientY);
    setIsDragging(true);
  };

  // Handle drag move
  const handleDragMove = (clientY: number) => {
    if (dragStartY !== null && isDragging) {
      const deltaY = clientY - dragStartY;
      if (deltaY > 0) {
        setDragCurrentY(clientY);
      }
    }
  };

  // Handle drag end
  const handleDragEnd = () => {
    if (dragStartY !== null && dragCurrentY !== null) {
      const deltaY = dragCurrentY - dragStartY;
      if (deltaY > DRAG_THRESHOLD_PX) {
        closePanel();
      }
    }
    setDragStartY(null);
    setDragCurrentY(null);
    setIsDragging(false);
  };

  // Calculate translation for visual feedback during drag
  const getTranslateY = () => {
    if (dragStartY !== null && dragCurrentY !== null && isDragging) {
      const deltaY = dragCurrentY - dragStartY;
      return deltaY > 0 ? deltaY : 0;
    }
    return 0;
  };

  // Add global mouse listeners when dragging
  useEffect(() => {
    if (!isDragging) return;

    const handleGlobalMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      handleDragMove(e.clientY);
    };

    const handleGlobalMouseUp = () => {
      handleDragEnd();
    };

    document.addEventListener("mousemove", handleGlobalMouseMove);
    document.addEventListener("mouseup", handleGlobalMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleGlobalMouseMove);
      document.removeEventListener("mouseup", handleGlobalMouseUp);
    };
  }, [isDragging, dragStartY, dragCurrentY]);

  // Placeholder content - user will replace this later
  const renderContent = () => (
    <NewPrescriptionWidget editingPrescription={editingPrescription} />
  );

  const isEditMode = !!editingPrescription;

  return (
    <>
      {/* Backdrop for edit mode */}
      {isDesktop && (
        <Backdrop
          $isVisible={isOpen && isEditMode}
          onClick={closePanel}
        />
      )}

      {/* Desktop: Slide-in from right */}
      {isDesktop && (
        <DesktopPanelWrapper $isOpen={isOpen} theme={theme}>
          {renderContent()}
        </DesktopPanelWrapper>
      )}

      {/* Mobile: Bottom sheet drawer */}
      {!isDesktop && (
        <SwipeableDrawer
          anchor="bottom"
          open={isOpen}
          onClose={closePanel}
          onOpen={() => {}}
          disableSwipeToOpen={true}
          hideBackdrop={true}
          disableScrollLock={true}
          disableEnforceFocus={true}
          disableAutoFocus={true}
          disableRestoreFocus={true}
          ModalProps={{
            keepMounted: true,
            disablePortal: false,
            BackdropProps: {
              style: {
                backgroundColor: "transparent",
                pointerEvents: "none",
              },
            },
            style: {
              pointerEvents: "none",
            },
          }}
          PaperProps={{
            sx: {
              height: BOTTOM_SHEET_HEIGHT,
              borderTopLeftRadius: "26px",
              borderTopRightRadius: "26px",
              overflow: "hidden",
              pointerEvents: "auto",
              boxShadow: "0 -6px 7.6px 4px rgba(0, 0, 0, 0.10)",
              transform: `translateY(${getTranslateY()}px)`,
              transition: isDragging ? "none" : "transform 0.3s ease-out",
              backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
            },
          }}
        >
          <MobileBottomSheetContent>
            <DragHandleContainer
              $isDragging={isDragging}
              theme={theme}
              onMouseDown={(e) => {
                e.preventDefault();
                handleDragStart(e.clientY);
              }}
              onTouchStart={(e) => {
                handleDragStart(e.touches[0].clientY);
              }}
              onTouchMove={(e) => {
                if (isDragging) {
                  handleDragMove(e.touches[0].clientY);
                }
              }}
              onTouchEnd={handleDragEnd}
            >
              <DragHandle theme={theme} />
            </DragHandleContainer>
            <MobileBottomSheetBody>{renderContent()}</MobileBottomSheetBody>
          </MobileBottomSheetContent>
        </SwipeableDrawer>
      )}
    </>
  );
};

export default ClientSidePanel;
